
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { UserSegmentProvider } from "./context/UserSegmentContext";
import Dashboard from "./components/Dashboard";
import UserList from './components/users/UserList';
import AppAccessLogRedirect from './components/users/AppAccessLogRedirect';
import DailyActiveUsers from './components/active-users/DailyActiveUsers';
import VendingCounts from './components/installationData/VendingCounts';
import HeartRateSessionList from './components/heart-rate-sessions/HeartRateSessionList';
import UserHeartRateSessionsCount from './components/heart-rate-sessions/UserHeartRateSessionsCount';
import UserPauseCount from './components/pause/UserPauseCount';
import BreathList from './components/pause/BreathList';
import MeditationList from './components/pause/MeditationList';
import MonitorSessionList from './components/monitor-sessions/MonitorSessionList';
import MonitorSessionData from './components/monitor-sessions/MonitorSessionData';
import UserPermissions from './components/user-permissions/UserPermissions';
import FeedbackResponseDetail from './components/feedback/FeedbackResponseDetail';
import CeoRequestDetail from './components/ceo-requests/CeoRequestDetail';
import UserFeedback from './components/user-feedback/UserFeedback';
import SurveyResponsePage from './components/survey/SurveyResponsePage';
import SignupSurveyResponsePage from './components/signup-survey/SignupSurveyResponsePage';
import UserSubscriptionList from './components/subscription/List';
import UserSubscriptionHistoryList from './components/subscription/History';
import ReportHistory from './components/ReportHistory';
import EventLogs from './components/EventLogs';
import MostActiveUsers from './components/active-users/MostActiveUsers';
import CohortDetails from './components/active-users/CohortDetails';
import FreeGrantsPage from './components/free-grants/FreeGrantsPage';
import Download from './components/QrCode/Qrcode'; 
import AppDownload from "./components/download/Download";
import Login from "./components/Login"; // Uncomment when needed
import BioVital from "./components/BioVital";
import UserBioVitalsCount from "./components/bio-vitals/UserBioVitalsCount";
import BPDetailsList from "./components/bio-vitals/BPDetailsList";
import SugarDetailsList from "./components/bio-vitals/SugarDetailsList";
import OxygenDetailsList from "./components/bio-vitals/OxygenDetailsList";
import SaltDetailsList from "./components/intake/SaltDetailsList";
import WaterDetailsList from "./components/intake/WaterDetailsList";
import UserMedicineCount from "./components/medicine/UserMedicineCount";
import MedicineLogDetailsList from "./components/medicine/MedicineLogDetailsList";
import MedicineMasterDetailsList from "./components/medicine/MedicineMasterDetailsList";
import UserSleepSessionCount from "./components/sleep-sessions/UserSleepSessionCount";
import SleepSessionDataList from "./components/sleep-sessions/SleepSessionDataList";
import CloudwatchErrorLogs from "./components/cloudwatch-logs/CloudwatchErrorLogs";
import WebhookActivityDashboard from "./components/webhook-activity/WebhookActivityDashboard";
import DeleteUserData from "./components/DeleteUserData/DeleteUserData";
import StandingCheckSessionList from "./components/standing-check/StandingCheckSessionList";
import PauseAlertImpactList from "./components/pause-alert-impact/PauseAlertImpactList";
import ContinuousUsageStreaks from "./components/continuous-usage/ContinuousUsageStreaks";
import ContinuousUsageStreaksDetail from "./components/continuous-usage/ContinuousUsageStreaksDetail";
import PauseAlertImpactDetail from "./components/pause-alert-impact/PauseAlertImpactDetail";
import UserDetail from "./components/user-detail/UserDetail";
import StandingCheckDetailList from "./components/standing-check/StandingCheckDetailList";
import WorkoutSessionList from "./components/workout-data/WorkoutSessionList";
import WorkoutDetailList from "./components/workout-data/WorkoutDetailList";
import WorkoutRecordList from "./components/workout-data/WorkoutRecordList";
import NotificationList from './components/notifications/NotificationList';
import BigQueryInstallEvents from './components/bigquery-install-events/BigQueryInstallEvents';
import Installs from './components/installs/Installs';
import SupportPortal from './components/support-portal/SupportPortal';
import SleepSummaryList from './components/sleep-summary/SleepSummaryList';
import SleepSummaryDetail from './components/sleep-summary/SleepSummaryDetail';

/**
 * URLS — Application route configuration component.
 *
 * Wraps the entire app in a `BrowserRouter` and declares all client-side
 * routes via React Router v6 `<Routes>`. Each `<Route>` maps a URL path to
 * its corresponding page component.
 *
 * Route map:
 * | Path                              | Component                    | Notes                        |
 * |-----------------------------------|------------------------------|------------------------------|
 * | `/`                               | Login                        | Entry / auth gate            |
 * | `/dashboard`                      | Dashboard                    | Main overview                |
 * | `/users`                          | UserList                     |                              |
 * | `/user-detail/:encodedUserId`     | UserDetail                   | User 360 profile, btoa id    |
 * | `/survey-response/:responseId`    | SurveyResponsePage           | one survey response, in full |
 * | `/signup-survey-response/:id`     | SignupSurveyResponsePage     | one signup-survey response   |
 * | `/user-activity-logs/:id`         | AppAccessLogRedirect         | → /user-detail?tab=app-access|
 * | `/active-users`                   | DailyActiveUsers             | Daily active users (redesign)|
 * | `/unique-users-count`             | MostActiveUsers              | Most active users (redesign) |
 * | `/installation-data-statics`      | VendingCounts                |                              |
 * | `/bigquery-install-events`        | BigQueryInstallEvents        | GA4 install events, BigQuery sync |
 * | `/installs`                       | Installs                     | Install funnel over synced GA4 events |
 * | `/installs-bigquery`              | Installs (source=bigquery)   | The same funnel straight from BigQuery — Installation Statistics' View |
 * | `/pauser-support`                 | SupportPortal                | iframe of pauser-574e1.web.app |
 * | `/heart-rate-sessions/:id?`       | HeartRateSessionList         | Optional user filter         |
 * | `/user-heart-rate-sessions`       | UserHeartRateSessionsCount   |                              |
 * | `/user-pause-count`               | UserPauseCount               |                              |
 * | `/breath-list/:id?`               | BreathList                   | Optional user filter         |
 * | `/pauses-list/:id?`               | MeditationList               | Optional user filter         |
 * | `/user-monitor-sessions`          | MonitorSessionList           |                              |
 * | `/monitor-sessions/:id?`          | MonitorSessionData           | Optional session filter      |
 * | `/user-permissions`               | UserPermissions              | Grant matrix redesign (022)  |
 * | `/user-subscription`              | UserSubscriptionList         |                              |
 * | `/subscription/history/:id?`      | UserSubscriptionHistoryList  | Optional user filter         |
 * | `/report-history`                 | ReportHistory                |                              |
 * | `/event-logs`                     | EventLogs                    |                              |
 * | `/bio-vital`                      | BioVital                     |                              |
 * | `/user-bio-vitals`                | UserBioVitalsCount           |                              |
 * | `/bp-details/:id?`                | BPDetailsList                | Optional user filter         |
 * | `/sugar-details/:id?`             | SugarDetailsList             | Optional user filter         |
 * | `/oxygen-details/:id?`            | OxygenDetailsList            | Optional user filter         |
 * | `/salt-details/:id?`              | SaltDetailsList              | Optional user filter         |
 * | `/water-details/:id?`             | WaterDetailsList             | Optional user filter         |
 * | `/pause-alert-impact`             | PauseAlertImpactList         | Breath vs alert impact       |
 * | `/pause-alert-impact-details/:encodedUserId` | PauseAlertImpactDetail | Drill-down, btoa user id  |
 * | `/user-medicine`                  | UserMedicineCount            |                              |
 * | `/medicine-log-details/:id?`      | MedicineLogDetailsList       | Optional user filter         |
 * | `/medicine-master-details/:id?`   | MedicineMasterDetailsList    | Optional user filter         |
 * | `/firebase-feedback`              | FeedbackList                 |                              |
 * | `/QR-download`                    | Download                     | QR code download page        |
 * | `/download`                       | AppDownload                  | App download page            |
 *
 * @component
 * @returns {JSX.Element} The fully configured `BrowserRouter` with all routes.
 */
const URLS = () => {
  return (
    <BrowserRouter>
      <UserSegmentProvider>
      <Routes>
        {/* Uncomment and add other routes like Login when needed */}
        <Route path="/" element={<Login />} />
        {/* <Route path="/register" element={<Register />} /> */}
        <Route path="/users" element={<UserList />} />
        <Route path="/free-grants" element={<FreeGrantsPage />} />
        <Route path="/user-detail/:encodedUserId" element={<UserDetail />} />
        <Route path="/dashboard" element={<Dashboard />} />
        {/* Retired page — the app-access log is now a User 360 tab (keeps old bookmarks alive). */}
        <Route path="/user-activity-logs/:id" element={<AppAccessLogRedirect />} />
        <Route path="/active-users" element={<DailyActiveUsers />} />
        <Route path="/unique-users-count" element={<MostActiveUsers />} />
        <Route path="/cohort-details" element={<CohortDetails />} />
        <Route path="/installation-data-statics" element={<VendingCounts />} />
        <Route path="/heart-rate-sessions/:id?" element={<HeartRateSessionList />} />
        <Route path="/user-heart-rate-sessions" element={<UserHeartRateSessionsCount />} />
        <Route path="/user-pause-count" element={<UserPauseCount />} />
        <Route path="/breath-list/:id?" element={<BreathList />} />
        <Route path="/pauses-list/:id?" element={<MeditationList />} />
        <Route path="/user-monitor-sessions?" element={<MonitorSessionList />} />
        <Route path="/monitor-sessions/:id?" element={<MonitorSessionData />} />
        <Route path="/user-permissions" element={<UserPermissions />} />
        <Route path="/user-subscription" element={<UserSubscriptionList />} />
        <Route path="/subscription/history/:id?" element={<UserSubscriptionHistoryList />} />
        <Route path="/report-history" element={<ReportHistory />} />
        {/* Day-one feedback (063). The detail is a real route, not a modal, so Back
            returns to the list with its filters intact. */}
        <Route path="/feedback-responses/:responseId" element={<FeedbackResponseDetail />} />

        {/* CEO Requests (066) — read of the Firestore collection the app writes. Detail is a
            real route, not a modal, so Back returns to the list with its filters intact. */}
        <Route path="/ceo-requests/:requestId" element={<CeoRequestDetail />} />

        {/* User Feedback (067) — one screen, one tab per source. The screens this replaced keep
            their URLs as redirects so existing bookmarks and the operator guide still work; the
            active tab lives in `?tab=` so it can be linked and survives a refresh.
            `/feedback-responses` no longer names a tab: Second Day Feedback was removed, so the
            redirect lands on the screen's default tab rather than a dead `?tab=` value. */}
        <Route path="/user-feedback" element={<UserFeedback />} />
        {/* One survey response as its own page — linkable, refreshable, sendable. Replaced the
            detail modal on the Survey Responses tab. */}
        <Route path="/survey-response/:responseId" element={<SurveyResponsePage />} />
        <Route path="/signup-survey-response/:responseId" element={<SignupSurveyResponsePage />} />
        <Route path="/firebase-feedback" element={<Navigate replace to="/user-feedback?tab=firebase" />} />
        <Route path="/survey-responses" element={<Navigate replace to="/user-feedback?tab=survey" />} />
        <Route path="/feedback-responses" element={<Navigate replace to="/user-feedback" />} />
        <Route path="/ceo-requests" element={<Navigate replace to="/user-feedback?tab=ceo" />} />

        <Route path="/event-logs" element={<EventLogs />} />
        <Route path="/bio-vital" element={<BioVital />} />
        <Route path="/user-bio-vitals" element={<UserBioVitalsCount />} />
        <Route path="/bp-details/:id?" element={<BPDetailsList />} />
        <Route path="/sugar-details/:id?" element={<SugarDetailsList />} />
        <Route path="/oxygen-details/:id?" element={<OxygenDetailsList />} />
        <Route path="/salt-details/:id?" element={<SaltDetailsList />} />
        <Route path="/water-details/:id?" element={<WaterDetailsList />} />
        <Route path="/user-medicine" element={<UserMedicineCount />} />
        <Route path="/medicine-log-details/:id?" element={<MedicineLogDetailsList />} />
        <Route path="/medicine-master-details/:id?" element={<MedicineMasterDetailsList />} />
        <Route path="/user-sleep-sessions" element={<UserSleepSessionCount />} />
        <Route path="/sleep-session-details/:encodedUuid/:encodedUserId" element={<SleepSessionDataList />} />
        <Route path="/cloudwatch-error-logs" element={<CloudwatchErrorLogs />} />
        <Route path="/webhook-activity" element={<WebhookActivityDashboard />} />
        <Route path="/delete-user-data" element={<DeleteUserData />} />
        <Route path="/standing-check-sessions" element={<StandingCheckSessionList />} />
        <Route path="/pause-alert-impact" element={<PauseAlertImpactList />} />
        <Route path="/continuous-usage-streaks" element={<ContinuousUsageStreaks />} />
        <Route path="/continuous-usage-streaks-details/:encodedUserId" element={<ContinuousUsageStreaksDetail />} />
        <Route path="/pause-alert-impact-details/:encodedUserId" element={<PauseAlertImpactDetail />} />
        <Route path="/standing-check-details/:encodedUserId" element={<StandingCheckDetailList />} />
        <Route path="/workout-sessions" element={<WorkoutSessionList />} />
        <Route path="/workout-details/:encodedUserId" element={<WorkoutDetailList />} />
        <Route path="/workout-records" element={<WorkoutRecordList />} />
        <Route path="/notifications" element={<NotificationList />} />
        <Route path="/bigquery-install-events" element={<BigQueryInstallEvents />} />
        <Route path="/installs" element={<Installs />} />
        <Route path="/installs-bigquery" element={<Installs source="bigquery" />} />
        <Route path="/pauser-support" element={<SupportPortal />} />
        <Route path="/user-sleep-summary" element={<SleepSummaryList />} />
        <Route path="/user-sleep-summary-details/:encodedUserId" element={<SleepSummaryDetail />} />
        <Route path="/QR-download" element={<Download />} />
        <Route path="/download" element={<AppDownload />} />
        {/* Add more routes as needed */}
        
      </Routes>
      </UserSegmentProvider>
    </BrowserRouter>
  );
}

export default URLS;
